import { shade } from '../pixel.js';
import type { PixelCanvas } from '../pixel.js';
import { box, drawBox, drawBoxes, drawCylinder, faceQuad, faces, frontFace, iso } from './iso.js';
import type { Face, FurniDrawer } from './iso.js';

/*
 * Arcade cabinets, the dragon boat and funfair props. Owned by the arcade
 * feature. Built from the shared isometric helpers in ./iso.ts.
 */

/** A little bitmap painted onto a cabinet face; each character keys `palette`. */
function faceBitmap(
  pc: PixelCanvas,
  face: Face,
  at: number,
  rows: readonly string[],
  palette: Record<string, string>,
  topH: number,
  cell = 0.03,
  step = 2,
): void {
  const width = rows[0]!.length;
  const s0 = -(width * cell) / 2;
  rows.forEach((row, ry) => {
    [...row].forEach((ch, rx) => {
      const color = palette[ch];
      if (!color) return;
      const s = s0 + rx * cell;
      faceQuad(pc, face, at, s, s + cell, topH - (ry + 1) * step, topH - ry * step, color);
    });
  });
}

interface CabinetStyle {
  body: string;
  trim: string;
  marquee: string;
  screen: string;
  art: readonly string[];
  palette: Record<string, string>;
  buttons: readonly string[];
}

/** A classic upright cabinet: body, marquee, screen with the game's art, and buttons. */
function cabinet(style: CabinetStyle): FurniDrawer {
  return (pc, rotation) => {
    const half = 0.32;
    drawBox(pc, box(-half, half, -half, half, 0, 64, faces(style.body)));
    // Marquee cap on top.
    drawBox(pc, box(-half - 0.02, half + 0.02, -half - 0.02, half + 0.02, 64, 70, faces(style.trim)));
    const face = frontFace(rotation);
    if (!face) {
      // Seen from behind: vents and a service panel.
      for (const f of ['left', 'right'] as const) {
        faceQuad(pc, f, half, -0.2, 0.2, 14, 40, shade(style.body, 0.8));
        for (let h = 44; h < 58; h += 4) faceQuad(pc, f, half, -0.16, 0.16, h, h + 1, shade(style.body, 0.6));
      }
      return;
    }
    const at = half;
    faceQuad(pc, face, at, -0.3, 0.3, 56, 63, style.marquee);
    faceQuad(pc, face, at, -0.24, 0.24, 57, 62, shade(style.marquee, 1.18));
    // Screen bezel and screen.
    faceQuad(pc, face, at, -0.27, 0.27, 28, 54, '#1b1b24');
    faceQuad(pc, face, at, -0.23, 0.23, 31, 51, style.screen);
    faceBitmap(pc, face, at, style.art, style.palette, 50);
    // Control deck: a jutting ledge with a joystick and buttons.
    const out = at + 0.08;
    if (face === 'right') drawBox(pc, box(at, out, -0.3, 0.3, 20, 25, faces(style.trim)));
    else drawBox(pc, box(-0.3, 0.3, at, out, 20, 25, faces(style.trim)));
    const deck = (s: number) => (face === 'right' ? iso(at + 0.04, s, 25) : iso(s, at + 0.04, 25));
    const stick = deck(-0.16);
    pc.rect(stick.x, stick.y - 5, 1, 5, '#2a2a2a');
    pc.rect(stick.x - 1, stick.y - 7, 3, 2, '#e8574b');
    style.buttons.forEach((color, i) => {
      const p = deck(0.02 + i * 0.1);
      pc.rect(p.x - 1, p.y - 2, 3, 2, color);
    });
    // Coin slot.
    faceQuad(pc, face, at, -0.05, 0.05, 8, 14, '#2c2c2c');
    faceQuad(pc, face, at, -0.01, 0.01, 9, 13, '#f0bf36');
  };
}

const drawKopiCatch = cabinet({
  body: '#8a5330',
  trim: '#f0bf36',
  marquee: '#e8574b',
  screen: '#fbe7b6',
  art: ['....s.s...', '.....s.s..', '.wwwwwww..', '.wbbbbbwww', '.wbbbbbw.w', '.wbbbbbwww', '..wbbbw...', '...www....'],
  palette: { s: '#9aa0a8', w: '#fffaf0', b: '#6b3a1c' },
  buttons: ['#f0bf36', '#3d9e4c'],
});

const drawMrtDash = cabinet({
  body: '#c8423b',
  trim: '#e9e6df',
  marquee: '#2a7f3a',
  screen: '#8fd3f4',
  art: ['..........', '.gggggggg.', '.gwwgwwgg.', '.gwwgwwgg.', '.rrrrrrrr.', '.gggggggg.', '..k....k..', 'dddddddddd'],
  palette: { g: '#e8e8e8', w: '#5ab4e6', r: '#d9433b', k: '#2a2a2a', d: '#6b6b6b' },
  buttons: ['#e8574b', '#3a6fd6'],
});

const drawKuehStack = cabinet({
  body: '#2aa894',
  trim: '#f19bb7',
  marquee: '#f0bf36',
  screen: '#fff4d6',
  art: ['...gg.....', '...pppp...', '..gggggg..', '..pppppp..', '..gggggg..', '..pppppp..', '.bbbbbbbb.', '.bbbbbbbb.'],
  palette: { g: '#3d9e4c', p: '#f19bb7', b: '#b8691e' },
  buttons: ['#f19bb7', '#f0bf36'],
});

const drawChope = cabinet({
  body: '#3a6fd6',
  trim: '#f0bf36',
  marquee: '#e8574b',
  screen: '#d8c9a8',
  art: ['..........', '...wwww...', '...wkkw...', '...wwww...', '.tttttttt.', '..t....t..', '..t....t..', '.........'],
  palette: { w: '#fffaf0', k: '#7fb7d9', t: '#8a5330' },
  buttons: ['#fffaf0', '#e8574b', '#3d9e4c'],
});

/** The dragon boat, lying along the tile; its head points the way it faces. */
const drawDragonBoat: FurniDrawer = (pc, rotation) => {
  const alongU = rotation === 2 || rotation === 6;
  const dir = rotation === 2 || rotation === 4 ? 1 : -1;
  // (a, b) run along and across the boat; map them into tile space.
  const at = (a: number, b: number, h: number) => (alongU ? iso(a * dir, b, h) : iso(b, a * dir, h));
  const span = (a0: number, a1: number, b0: number, b1: number, h0: number, h1: number, color: string, top?: string) => {
    const [x0, x1] = [a0 * dir, a1 * dir].sort((p, q) => p - q) as [number, number];
    return alongU ? box(x0, x1, b0, b1, h0, h1, faces(color, top)) : box(b0, b1, x0, x1, h0, h1, faces(color, top));
  };
  const hull = '#c8423b';
  drawBoxes(pc, [
    span(-0.62, 0.55, -0.2, 0.2, 2, 12, hull, '#6b3a1c'),
    span(-0.7, -0.62, -0.12, 0.12, 4, 14, hull),
  ]);
  // Scales along the gunwale.
  for (let a = -0.55; a < 0.5; a += 0.12) {
    const p = at(a, 0.2, 9);
    const q = at(a, -0.2, 9);
    pc.rect(p.x, p.y, 2, 2, '#f0bf36');
    pc.rect(q.x, q.y, 2, 2, '#f0bf36');
  }
  // Benches for the paddlers.
  for (const a of [-0.36, -0.08, 0.2]) drawBox(pc, span(a, a + 0.06, -0.18, 0.18, 12, 14, '#b8691e'));
  // A drum at the stern.
  const drum = at(-0.5, 0, 0);
  pc.ellipse(drum.x, drum.y - 18, 5, 3, '#8a5330');
  pc.rect(drum.x - 5, drum.y - 18, 10, 5, '#8a5330');
  pc.ellipse(drum.x, drum.y - 18, 4, 2, '#f3e2b3');
  // The dragon's neck, head, eye and whiskers at the bow.
  const neck = at(0.55, 0, 12);
  const head = at(0.68, 0, 24);
  pc.poly(
    [
      { x: neck.x - 3, y: neck.y },
      { x: neck.x + 3, y: neck.y },
      { x: head.x + 3, y: head.y + 2 },
      { x: head.x - 3, y: head.y + 2 },
    ],
    '#2a8f4a',
  );
  pc.ellipse(head.x, head.y, 6, 5, '#3d9e4c');
  pc.rect(head.x - 3, head.y - 7, 2, 4, '#f0bf36');
  pc.rect(head.x + 2, head.y - 7, 2, 4, '#f0bf36');
  pc.rect(head.x - 2, head.y - 1, 2, 2, '#fffaf0');
  pc.px(head.x - 1, head.y - 1, '#1d1716');
  pc.rect(head.x - 4, head.y + 3, 8, 2, '#e8574b');
  pc.line(head.x + 4, head.y + 2, head.x + 9, head.y + 4, '#f0bf36');
  // Tail flick.
  const tail = at(-0.72, 0, 20);
  pc.poly(
    [
      { x: tail.x - 2, y: tail.y + 6 },
      { x: tail.x + 2, y: tail.y + 6 },
      { x: tail.x + 4, y: tail.y - 2 },
    ],
    '#3d9e4c',
  );
};

const drawPopcornCart: FurniDrawer = (pc, rotation) => {
  // Wheels, then the striped cart, the glass case of popcorn and a canopy.
  for (const [u, v] of [
    [-0.26, 0.3],
    [0.3, -0.26],
  ] as const) {
    const w = iso(u, v, 5);
    pc.ellipse(w.x, w.y, 4, 5, '#2a2a2a');
    pc.ellipse(w.x, w.y, 2, 2, '#9aa0a8');
  }
  drawBox(pc, box(-0.28, 0.28, -0.28, 0.28, 5, 30, faces('#e8574b')));
  // The brighter stripe and the price sign track whichever side faces the customer.
  const face = frontFace(rotation) ?? 'left';
  const otherFace = face === 'left' ? 'right' : 'left';
  for (const s of [-0.18, 0, 0.18]) {
    faceQuad(pc, face, 0.28, s - 0.04, s + 0.04, 5, 30, '#fffaf0');
    faceQuad(pc, otherFace, 0.28, s - 0.04, s + 0.04, 5, 30, '#efe6d6');
  }
  drawBox(pc, box(-0.24, 0.24, -0.24, 0.24, 30, 54, faces('#cfeaf5', '#e8f6fb')));
  for (let i = 0; i < 26; i += 1) {
    const u = -0.2 + ((i * 7) % 9) * 0.05;
    const v = -0.2 + ((i * 5) % 9) * 0.05;
    const p = iso(u, v, 32 + ((i * 3) % 8));
    pc.rect(p.x, p.y, 2, 2, i % 3 === 0 ? '#f0bf36' : '#fff4d6');
  }
  pc.rect(iso(0, 0, 60).x, iso(0, 0, 60).y, 1, 6, '#9aa0a8');
  drawBox(pc, box(-0.34, 0.34, -0.34, 0.34, 60, 64, faces('#fffaf0')));
  for (const s of [-0.24, 0, 0.24]) {
    faceQuad(pc, face, 0.34, s - 0.06, s + 0.06, 60, 64, '#e8574b');
    faceQuad(pc, otherFace, 0.34, s - 0.06, s + 0.06, 60, 64, '#c23e33');
  }
  const sign = face === 'left' ? iso(0.29, 0.29, 22) : iso(-0.29, 0.29, 22);
  pc.rect(sign.x - 6, sign.y - 4, 12, 6, '#f0bf36');
  pc.rect(sign.x - 4, sign.y - 2, 8, 2, '#8a5330');
};

const BALLOONS = ['#e8574b', '#f0bf36', '#3a6fd6', '#3d9e4c', '#f19bb7', '#8a52c8', '#ef7d2c'];

const drawBalloonStand: FurniDrawer = (pc) => {
  drawCylinder(pc, 0.16, 0, 5, '#b8691e', '#8a5330');
  pc.rect(-1, -52, 2, 48, '#9aa0a8');
  const spots = [
    [-9, -86],
    [0, -92],
    [9, -86],
    [-12, -74],
    [-3, -78],
    [7, -74],
    [14, -66],
  ] as const;
  spots.forEach(([x, y]) => pc.line(0, -52, x, y + 6, '#e9e6df'));
  spots.forEach(([x, y], i) => {
    const color = BALLOONS[i % BALLOONS.length]!;
    pc.ellipse(x, y, 6, 7, color);
    pc.rect(x - 3, y - 4, 2, 2, shade(color, 1.35));
    pc.rect(x - 1, y + 7, 2, 1, shade(color, 0.7));
  });
};

const drawCarouselHorse: FurniDrawer = (pc, rotation) => {
  drawCylinder(pc, 0.3, 0, 4, '#f0bf36', '#b8691e');
  // The brass pole runs through the saddle.
  pc.rect(-1, -78, 3, 76, '#e5a21a');
  pc.rect(1, -78, 1, 76, '#b8691e');
  pc.ellipse(0, -80, 3, 2, '#e5a21a');
  const alongU = rotation === 2 || rotation === 6;
  const dir = rotation === 2 || rotation === 4 ? 1 : -1;
  const at = (a: number, h: number) => (alongU ? iso(a * dir, 0, h) : iso(0, a * dir, h));
  const body = at(0, 26);
  const head = at(0.26, 38);
  const tail = at(-0.24, 28);
  // Legs mid-gallop.
  for (const [a, lean] of [
    [0.14, 3],
    [-0.14, -3],
  ] as const) {
    const hip = at(a, 22);
    pc.line(hip.x, hip.y, hip.x + lean, hip.y + 10, '#f4efe4');
    pc.line(hip.x + 1, hip.y, hip.x + lean + 1, hip.y + 10, '#f4efe4');
  }
  pc.line(tail.x, tail.y, tail.x - (head.x - body.x) / 3, tail.y + 8, '#f19bb7');
  pc.ellipse(body.x, body.y, 12, 6, '#f4efe4');
  pc.ellipse(head.x, head.y, 5, 4, '#f4efe4');
  pc.line(body.x + (head.x - body.x) / 2, body.y - 2, head.x, head.y, '#f4efe4');
  pc.rect(head.x - 1, head.y - 6, 2, 3, '#f4efe4');
  pc.px(head.x + (head.x > body.x ? 2 : -2), head.y - 1, '#1d1716');
  // Mane, saddle and bridle.
  pc.line(head.x, head.y - 4, body.x + (head.x - body.x) / 3, body.y - 5, '#f19bb7');
  pc.ellipse(body.x, body.y - 4, 5, 3, '#e8574b');
  pc.rect(body.x - 5, body.y - 2, 10, 2, '#f0bf36');
  pc.px(head.x, head.y + 1, '#3a6fd6');
};

const BULBS = ['#ffd24a', '#ff6b5e', '#6be3ff', '#8dff7a', '#ff9bd2'];

const drawStringLights: FurniDrawer = (pc, rotation) => {
  drawCylinder(pc, 0.1, 0, 4, '#6b6b6b', '#4a4a4a');
  pc.rect(-1, -96, 3, 92, '#6b4226');
  pc.rect(1, -96, 1, 92, '#4a2d1a');
  // A sagging run of bulbs off the pole, towards the rotation's side.
  const side = rotation === 2 || rotation === 6 ? 1 : -1;
  const run = rotation === 0 || rotation === 2 ? 1 : -1;
  const end = { x: side * run * 34, y: -76 };
  for (let i = 0; i <= 12; i += 1) {
    const t = i / 12;
    const x = Math.round(end.x * t);
    const y = Math.round(-94 + (end.y + 94) * t + Math.sin(t * Math.PI) * 12);
    pc.px(x, y, '#2a2a2a');
    if (i % 2 === 1) {
      pc.rect(x - 1, y + 1, 3, 3, BULBS[(i >> 1) % BULBS.length]!);
      pc.px(x - 1, y + 1, '#fffaf0');
    }
  }
  pc.rect(end.x - 1, end.y - 1, 3, 3, '#6b4226');
};

const diamond = (pc: PixelCanvas, r: number, color: string) =>
  pc.poly([iso(-r, -r), iso(r, -r), iso(r, r), iso(-r, r)], color);

const drawSandMat: FurniDrawer = (pc) => {
  diamond(pc, 0.5, '#e8cf8f');
  diamond(pc, 0.44, '#f2dca0');
  for (const [u, v, c] of [
    [-0.3, -0.1, '#d9bb74'],
    [0.2, -0.3, '#d9bb74'],
    [0.1, 0.25, '#fff0c4'],
    [-0.15, 0.3, '#d9bb74'],
    [0.32, 0.08, '#fff0c4'],
    [-0.05, -0.05, '#f7a6a0'],
  ] as const) {
    const p = iso(u, v);
    pc.rect(p.x, p.y, 2, 1, c);
  }
};

const drawSeaWater: FurniDrawer = (pc) => {
  diamond(pc, 0.5, '#2f8fcf');
  diamond(pc, 0.44, '#47a8e0');
  for (const [u, v] of [
    [-0.25, -0.05],
    [0.15, -0.25],
    [0.05, 0.22],
  ] as const) {
    const p = iso(u, v);
    pc.rect(p.x - 3, p.y, 3, 1, '#bfe8fa');
    pc.rect(p.x, p.y - 1, 3, 1, '#bfe8fa');
  }
};

export const ARCADE_DRAWERS: Record<string, FurniDrawer> = {
  arcade_kopi_catch: drawKopiCatch,
  arcade_mrt_dash: drawMrtDash,
  arcade_kueh_stack: drawKuehStack,
  arcade_chope: drawChope,
  dragon_boat: drawDragonBoat,
  popcorn_cart: drawPopcornCart,
  balloon_stand: drawBalloonStand,
  carousel_horse: drawCarouselHorse,
  string_lights: drawStringLights,
  sand_mat: drawSandMat,
  sea_water: drawSeaWater,
};
